import { useState, useEffect, useCallback } from 'react';
import { nominaService } from 'services/nominaService';
import { NominaResponse, GenerarNominaPayload, AprobarNominaPayload } from 'types/nomina.types';

export const useNominaMatricula = (anioEscolarId: number | null) => {
  const [nominas, setNominas]   = useState<NominaResponse[]>([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [acting, setActing]     = useState(false);
  const [actError, setActError] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    if (!anioEscolarId) return;
    setLoading(true); setError(null);
    try {
      setNominas(await nominaService.listarPorAnio(anioEscolarId));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cargar nóminas');
    } finally { setLoading(false); }
  }, [anioEscolarId]);

  useEffect(() => { cargar(); }, [cargar]);

  const wrap = async (fn: () => Promise<NominaResponse>, label: string) => {
    setActing(true); setActError(null);
    try {
      const updated = await fn();
      setNominas(prev => {
        const idx = prev.findIndex(n => n.idNomina === updated.idNomina);
        return idx >= 0 ? prev.map((n, i) => i === idx ? updated : n) : [...prev, updated];
      });
    } catch (e: unknown) {
      setActError(e instanceof Error ? e.message : `Error al ${label}`);
    } finally { setActing(false); }
  };

  const generar = (payload: GenerarNominaPayload) =>
    wrap(() => nominaService.generar(payload), 'generar nómina');

  const aprobar = (id: number, payload: AprobarNominaPayload) =>
    wrap(() => nominaService.aprobar(id, payload), 'aprobar nómina');

  const enviarUgel = (id: number) =>
    wrap(() => nominaService.enviarUgel(id), 'enviar a UGEL');

  const rectificar = (id: number, obs?: string) =>
    wrap(() => nominaService.rectificar(id, obs), 'rectificar nómina');

  return { nominas, loading, error, acting, actError, generar, aprobar, enviarUgel, rectificar, recargar: cargar };
};
